/**
 * Persona Routing — list view of every persona-routing AGENT_DECISION
 * episode across all playbooks for the selected client.
 *
 * Reads from /api/v1/creative-memory/decisions with
 * decision_type=persona_routing pre-applied. Renders each episode with
 * the typed PersonaRoutingDecisionCard so concept overlaps (with names),
 * per-persona rationale, and ranker telemetry are inspectable.
 */

import { useEffect, useMemo, useState } from 'react';
import { Route, RefreshCw, AlertCircle, Filter } from 'lucide-react';
import { clsx } from 'clsx';
import { useClientStore } from '@/stores/client';
import {
  creativeMemoryApi,
  type CreativeMemoryActivityItem,
} from '@/services/api';
import { PersonaRoutingDecisionCard } from './components/PersonaRoutingDecisionCard';

type Window = '24h' | '7d' | '30d' | 'all';

const windowToSince = (w: Window): string | undefined => {
  if (w === 'all') return undefined;
  const d = new Date();
  if (w === '24h') d.setDate(d.getDate() - 1);
  if (w === '7d') d.setDate(d.getDate() - 7);
  if (w === '30d') d.setDate(d.getDate() - 30);
  return d.toISOString();
};

export function PersonaRoutingPage() {
  const selectedClient = useClientStore((s) => s.selectedClient());
  const clientUid = selectedClient?.client_uid;

  const [items, setItems] = useState<CreativeMemoryActivityItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [window, setWindow] = useState<Window>('7d');
  const [filterCreative, setFilterCreative] = useState('');
  const [filterPersona, setFilterPersona] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    creativeMemoryApi
      .listDecisions({
        decision_type: 'persona_routing',
        client_uid: clientUid,
        since: windowToSince(window),
        limit: 100,
      })
      .then((resp) => {
        setItems(resp.items);
        setTotal(resp.total);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUid, window]);

  const filtered = useMemo(() => {
    const c = filterCreative.trim().toLowerCase();
    const p = filterPersona.trim().toLowerCase();
    return items.filter((it) => {
      if (c && !(it.creative_id || '').toLowerCase().includes(c)) return false;
      if (p) {
        const ids = [
          ...(it.selected_persona_ids || []),
          ...(it.candidate_persona_ids || []),
        ].map((s) => s.toLowerCase());
        if (!ids.some((id) => id.includes(p))) return false;
      }
      return true;
    });
  }, [items, filterCreative, filterPersona]);

  return (
    <div className="px-6 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Route className="w-5 h-5 text-violet-500" />
            <h1 className="text-xl font-semibold text-slate-800">
              Persona Routing
            </h1>
          </div>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            High-reasoning persona-ranking decisions emitted by the
            PersonaConceptGraphRanker on each creative-review backfill.
            Every card shows the ranking, per-persona rationale, the
            concept overlaps that drove the verdict, and ranker telemetry —
            so "why was persona X dropped from creative Y?" is answerable
            without re-running the pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <WindowSwitch value={window} onChange={setWindow} />
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-slate-300 disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-3 h-3', loading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </header>

      {/* Filter strip */}
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500">
          <Filter className="w-3 h-3" />
          Filter
        </span>
        <input
          type="text"
          value={filterCreative}
          onChange={(e) => setFilterCreative(e.target.value)}
          placeholder="creative_id contains…"
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal/40"
        />
        <input
          type="text"
          value={filterPersona}
          onChange={(e) => setFilterPersona(e.target.value)}
          placeholder="persona_id contains…"
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal/40"
        />
        <span className="ml-auto text-[10px] text-slate-500">
          {filtered.length} of {total} shown
        </span>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
          <div>
            <p className="font-semibold">Failed to load routing decisions</p>
            <p className="mt-0.5 text-rose-700">{error}</p>
          </div>
        </div>
      )}

      {!error && !loading && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center">
          <Route className="mx-auto w-8 h-8 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            No routing decisions in this window
          </p>
          <p className="mt-1 text-xs text-slate-500 max-w-md mx-auto">
            Trigger a creative-review backfill with{' '}
            <span className="font-mono">PERSONA_RANKER_MODE=llm</span> and{' '}
            <span className="font-mono">CREATIVE_MEMORY_ENABLED=true</span>, or
            widen the time window.
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((d) => (
            <PersonaRoutingDecisionCard key={d.episode_id} decision={d} />
          ))}
        </div>
      )}
    </div>
  );
}

interface WindowSwitchProps {
  value: Window;
  onChange: (w: Window) => void;
}

function WindowSwitch({ value, onChange }: WindowSwitchProps) {
  const options: { value: Window; label: string }[] = [
    { value: '24h', label: '24h' },
    { value: '7d', label: '7d' },
    { value: '30d', label: '30d' },
    { value: 'all', label: 'All' },
  ];
  return (
    <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 text-xs">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={clsx(
            'px-2.5 py-1 rounded transition',
            value === opt.value
              ? 'bg-brand-teal text-white'
              : 'text-slate-600 hover:text-slate-800',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
